import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { BetterDocQuestionnaireSelect } from "@/modules/patients/detail/better-doc/de/components/better-doc-consultation-questionnaire/better-doc-questionnaire-select"
import { BetterDocQuestionnaireTextField } from "@/modules/patients/detail/better-doc/de/components/better-doc-consultation-questionnaire/better-doc-questionnaire-text-field"
import { BetterDocQuestionnaireYesNoField } from "@/modules/patients/detail/better-doc/de/components/better-doc-consultation-questionnaire/better-doc-questionnaire-yes-no-field"
import { DeBetterDocSubSpecialtySelect } from "@/modules/patients/detail/better-doc/de/components/better-doc-consultation-questionnaire/de-better-doc-sub-specialty-select"
import { DeFixedBetterDocSpecialtyField } from "@/modules/patients/detail/better-doc/de/components/better-doc-consultation-questionnaire/de-fixed-better-doc-specialty-field"
import {
  BETTER_DOC_CONSULTATION_QUESTION_IDS_DECIDING_VISIBILITY,
  BETTER_DOC_CONSULTATION_SELECT_OPTIONS,
  type BetterDocConsultationAnswers,
  type BetterDocConsultationQuestion,
} from "@/modules/patients/detail/better-doc/de/lib/better-doc-consultation-questionnaire-definition"
import { getVisibleQuestions } from "@/modules/patients/detail/better-doc/de/lib/better-doc-consultation-questionnaire-visibility"
import type { DeBetterDocSearchFormType } from "@/modules/patients/detail/better-doc/de/schema/de-better-doc-search-schema"
import type { BetterDocSpecialty } from "@/modules/patients/detail/better-doc/lib/better-doc-sub-specialties"

type QuestionFieldProps = {
  question: BetterDocConsultationQuestion
  specialtyName: BetterDocSpecialty
}

const QuestionField = ({ question, specialtyName }: QuestionFieldProps) => {
  if (question.id === "subSpecialty") {
    return <DeBetterDocSubSpecialtySelect specialtyName={specialtyName} />
  }
  if (question.kind === "text") {
    return <BetterDocQuestionnaireTextField questionId={question.id} />
  }
  if (question.kind === "yesNo") {
    return <BetterDocQuestionnaireYesNoField questionId={question.id} />
  }
  const options = BETTER_DOC_CONSULTATION_SELECT_OPTIONS[question.id]
  return options ? <BetterDocQuestionnaireSelect questionId={question.id} options={options} /> : null
}

type BetterDocConsultationQuestionnaireProps = {
  specialtyName: BetterDocSpecialty
  isBetterDocSpecialtyFixed: boolean
}
export const BetterDocConsultationQuestionnaire = (props: BetterDocConsultationQuestionnaireProps) => {
  const { specialtyName, isBetterDocSpecialtyFixed } = props
  const { t } = useTranslation()
  const { control } = useFormContext<DeBetterDocSearchFormType>()
  const answersDecidingVisibility = useWatch({
    control,
    name: BETTER_DOC_CONSULTATION_QUESTION_IDS_DECIDING_VISIBILITY,
  })

  const answers: BetterDocConsultationAnswers = Object.fromEntries(
    BETTER_DOC_CONSULTATION_QUESTION_IDS_DECIDING_VISIBILITY.map((questionId, index) => [
      questionId,
      answersDecidingVisibility[index],
    ]),
  )
  const visibleQuestions = getVisibleQuestions(answers, specialtyName)

  return (
    <div className="grid w-full gap-4">
      <div className="text-on-surface text-base leading-6 font-medium">
        {t("episodeOfCare.betterDoc.searchDialog.medicalInformation")}
      </div>
      {isBetterDocSpecialtyFixed && <DeFixedBetterDocSpecialtyField specialtyName={specialtyName} />}
      {visibleQuestions.map((question) => (
        <QuestionField key={question.id} question={question} specialtyName={specialtyName} />
      ))}
    </div>
  )
}
